import { LifeBuoy, Loader2 } from 'lucide-react';
import { useSupport } from '@/contexts/SupportContext';
import { cn } from '@/lib/utils';

const STATUS_STYLES = {
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Resolved: 'bg-green-100 text-green-700',
  Closed: 'bg-gray-100 text-gray-500',
};

function formatRelativeTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

export function SupportTicketList() {
  const { tickets, loading, openThread, openNewRequest } = useSupport();

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (tickets.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
        <LifeBuoy className="h-12 w-12 text-gray-300 mb-3" />
        <h3 className="text-sm font-medium text-gray-900 mb-1">No support requests yet</h3>
        <p className="text-sm text-gray-500 mb-4">
          Have a question or need help? We're here for you.
        </p>
        <button
          onClick={openNewRequest}
          className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
        >
          Submit a Request
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <div className="divide-y divide-gray-100">
        {tickets.map((ticket) => (
          <button
            key={ticket.id}
            onClick={() => openThread(ticket)}
            className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
          >
            <div className="flex items-start justify-between gap-2 mb-1">
              <span className="text-sm font-medium text-gray-900 line-clamp-1">
                {ticket.subject || 'No subject'}
              </span>
              <span className={cn(
                'flex-shrink-0 text-xs font-medium px-2 py-0.5 rounded-full',
                STATUS_STYLES[ticket.status] || STATUS_STYLES.Open
              )}>
                {ticket.status || 'Open'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              {ticket.type && <span>{ticket.type}</span>}
              {ticket.type && <span>&middot;</span>}
              <span>#{ticket.id}</span>
              <span>&middot;</span>
              <span>{formatRelativeTime(ticket.updated_at || ticket.created_at)}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
